import { memo, useCallback, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ColumnConfig, CellRendererContext } from './types';
import type { EntityOption } from '@/hooks/useEntityOptions';
import { TextCell } from './cell-renderers/TextCell';
import { NumericCell } from './cell-renderers/NumericCell';
import { CheckboxCell } from './cell-renderers/CheckboxCell';
import { DateCell } from './cell-renderers/DateCell';
import { TimeCell } from './cell-renderers/TimeCell';
import { EntityCell } from './cell-renderers/EntityCell';
import { MarkdownCell } from './cell-renderers/MarkdownCell';
import { TextCellEditor } from './cell-editors/TextCellEditor';
import { NumericCellEditor } from './cell-editors/NumericCellEditor';
import { CheckboxCellEditor } from './cell-editors/CheckboxCellEditor';
import { DateCellEditor } from './cell-editors/DateCellEditor';
import { TimeCellEditor } from './cell-editors/TimeCellEditor';
import { EntityCellEditor } from './cell-editors/EntityCellEditor';
import { MarkdownCellEditor } from './cell-editors/MarkdownCellEditor';

interface EditableCellProps {
  /** Identifies the row this cell belongs to. Used to bind parent callbacks
   *  internally so the memoized cell isn't broken by inline-arrow props from
   *  the row renderer. */
  rowId: string;
  /** Identifies the column (== `column.data`). Same role as `rowId`. */
  colId: string;
  value: unknown;
  column: ColumnConfig;
  rowData: Record<string, unknown>;
  isEditing: boolean;
  readOnly: boolean;
  entityOptions?: EntityOption[];
  entityOptionsLoading?: boolean;
  locale?: string;
  /** Unbound parent callback. Bound to (rowId, colId) internally. */
  onStartEditing: (rowId: string, colId: string) => void;
  /** Unbound parent callback. Writes any field in this row (both the cell's
   *  own value and sibling fields go through this same hook). */
  onCellChange: (rowId: string, field: string, value: unknown) => void;
  /** Group multi-dispatch edits into a single undo step. */
  onStartUndoGroup: () => void;
  onEndUndoGroup: () => void;
  onCommit: () => void;
  onCancel: () => void;
}

/** Returns true if the column should use single-click to edit and show a chevron indicator */
function isDropdownCell(column: ColumnConfig, isReadOnly: boolean): boolean {
  if (isReadOnly) return false;
  if (column.singleClickEdit != null) return column.singleClickEdit;
  // Entity columns and columns with custom cellEditor default to dropdown behavior
  return column.type === 'entity' || !!column.cellEditor;
}

export const EditableCell = memo(function EditableCell({
  rowId,
  colId,
  value,
  column,
  rowData,
  isEditing,
  readOnly,
  entityOptions,
  entityOptionsLoading,
  locale,
  onStartEditing,
  onCellChange,
  onStartUndoGroup,
  onEndUndoGroup,
  onCommit,
  onCancel,
}: EditableCellProps) {
  // The `readOnly` prop is already fully resolved by the parent (column +
  // row-level + per-row predicate). Only honor the literal boolean `true`
  // fast-path here — never a *function* `readOnly` (always truthy), which the
  // parent already resolved into the prop; treating it as truthy would wrongly
  // lock the active cell.
  const isColumnReadOnly = column.readOnly === true || readOnly;
  const ariaLabel = column.header;
  const isDropdown = isDropdownCell(column, isColumnReadOnly);

  // Bind parent callbacks to this cell's (rowId, colId) once per identity
  // change. Cell editors keep their existing no-arg / (value) / (field, value)
  // signatures, and the bindings stay stable across DataRow re-renders — so
  // `EditableCell`'s memo actually skips re-renders when only the row above
  // re-rendered without changing this cell's inputs.
  const handleStartEditing = useCallback(
    () => onStartEditing(rowId, colId),
    [onStartEditing, rowId, colId],
  );
  const handleChange = useCallback(
    (next: unknown) => onCellChange(rowId, colId, next),
    [onCellChange, rowId, colId],
  );
  const handleSiblingFieldChange = useCallback(
    (field: string, next: unknown) => onCellChange(rowId, field, next),
    [onCellChange, rowId],
  );

  // What a live control in a `cellRenderer` gets to stage changes with —
  // the same row-write + undo-group callbacks an editor receives.
  const rendererContext = useMemo<CellRendererContext>(() => ({
    readOnly: isColumnReadOnly,
    setFieldValue: handleSiblingFieldChange,
    startUndoGroup: onStartUndoGroup,
    endUndoGroup: onEndUndoGroup,
  }), [isColumnReadOnly, handleSiblingFieldChange, onStartUndoGroup, onEndUndoGroup]);

  // Handle double-click to start editing (for non-dropdown cells)
  const handleDoubleClick = useCallback(() => {
    if (!isColumnReadOnly && !isDropdown) {
      handleStartEditing();
    }
  }, [isColumnReadOnly, isDropdown, handleStartEditing]);

  // Handle click on the chevron indicator to start editing
  const handleChevronClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isColumnReadOnly) {
      handleStartEditing();
    }
  }, [isColumnReadOnly, handleStartEditing]);

  // Checkbox is special - always renders as interactive (no separate edit mode)
  if (column.type === 'checkbox' && !isColumnReadOnly) {
    return (
      <CheckboxCellEditor
        value={value}
        onChange={(val) => {
          handleChange(val);
          onCommit();
        }}
      />
    );
  }

  // Editing mode — use custom cellEditor if provided
  if (isEditing && !isColumnReadOnly && column.cellEditor) {
    return (
      <>
        {column.cellEditor({
          value,
          rowData,
          onChange: handleChange,
          setSiblingFieldValue: handleSiblingFieldChange,
          startUndoGroup: onStartUndoGroup,
          endUndoGroup: onEndUndoGroup,
          onCommit,
          onCancel,
        })}
      </>
    );
  }

  // Editing mode
  if (isEditing && !isColumnReadOnly) {
    switch (column.type) {
      case 'numeric':
        return (
          <NumericCellEditor
            value={value}
            ariaLabel={ariaLabel}
            locale={locale}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
      case 'date':
        return (
          <DateCellEditor
            value={value}
            ariaLabel={ariaLabel}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
      case 'time':
        return (
          <TimeCellEditor
            value={value}
            ariaLabel={ariaLabel}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
      case 'entity':
        return (
          <EntityCellEditor
            value={value}
            options={entityOptions ?? []}
            allowCreate={column.entityConfig?.allowCreate}
            createLabel={column.entityConfig?.createLabel}
            optionGroup={column.entityConfig?.optionGroup}
            ariaLabel={ariaLabel}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
      case 'markdown':
        return (
          <MarkdownCellEditor
            value={value}
            ariaLabel={ariaLabel}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
      default:
        return (
          <TextCellEditor
            value={value}
            ariaLabel={ariaLabel}
            onChange={handleChange}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        );
    }
  }

  // Display mode wrapper with dropdown indicator
  const displayContent = column.cellRenderer
    ? column.cellRenderer(value, rowData, rendererContext)
    : renderDisplayCell(value, column, entityOptions, locale, entityOptionsLoading);

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className="min-h-[20px] flex items-center"
    >
      <div className="flex-1 min-w-0 truncate">{displayContent}</div>
      {isDropdown && (
        <button
          type="button"
          onClick={handleChevronClick}
          className="shrink-0 ml-1 p-0.5 rounded hover:bg-muted transition-colors"
          tabIndex={-1}
        >
          <ChevronDown className="h-3 w-3 text-muted-foreground/50" />
        </button>
      )}
    </div>
  );
});

function renderDisplayCell(value: unknown, column: ColumnConfig, entityOptions?: EntityOption[], locale?: string, entityOptionsLoading?: boolean) {
  switch (column.type) {
    case 'checkbox':
      return <CheckboxCell value={value} />;
    case 'numeric':
      return <NumericCell value={value} format={column.numericFormat?.pattern} locale={locale} />;
    case 'date':
      return <DateCell value={value} />;
    case 'time':
      return <TimeCell value={value} />;
    case 'entity':
      return <EntityCell value={value} options={entityOptions} isLoading={entityOptionsLoading} />;
    case 'markdown':
      return <MarkdownCell value={value} />;
    default:
      return <TextCell value={value} />;
  }
}
